import React, { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import catAlfabeto from '../assets/screens/cat-alfabeto.png';
import catNecessidades from '../assets/screens/cat-necessidades.png';
import catAlimentos from '../assets/screens/cat-alimentos.png';

// Todo texto aqui vem literalmente do app real
// (Conectar/app/src/main/res/layout/fragment_progress.xml e values/strings.xml).
// Os valores (contagens, %, gráfico) são exemplos ilustrativos que variam
// devagar só pra dar a sensação de dado vivo — os rótulos e a estrutura
// são exatamente os do projeto, nada foi inventado ali.

const topStats = [
  { label: 'Desenho mais reconhecido', value: 'Letra A' },
  { label: 'Rotina mais cheia', value: 'Manhã' },
  { label: 'Músicas mais tocadas', value: 'Piano Sonata Nº 14' },
];

const subcategoriesBase = [
  { icon: catAlfabeto, name: 'Alfabeto', count: 14 },
  { icon: catNecessidades, name: 'Necessidades', count: 9 },
  { icon: catAlimentos, name: 'Alimentos', count: 6 },
];

const gamesStatsBase = [
  { label: 'Taxa de acertos', color: '#03A9F4', suffix: '%' },
  { label: 'Tempo médio de resposta', color: '#00ED83', suffix: 's' },
  { label: 'Progresso geral', color: '#FF7C19', suffix: '%' },
];

const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const CHART_MAX = 5;

function randomWalk(value: number, amplitude: number, min: number, max: number) {
  const next = value + (Math.random() - 0.5) * 2 * amplitude;
  return Math.max(min, Math.min(max, Math.round(next)));
}

interface LiveData {
  subcategoryCounts: number[];
  gamesValues: number[];
  /** Um valor por mês — todos animam juntos, não só um mês fixo. */
  chartValues: number[];
}

const initialData: LiveData = {
  subcategoryCounts: subcategoriesBase.map((s) => s.count),
  gamesValues: [91, 0, 91],
  chartValues: [2, 3, 4, 2, 3, 5, 4, 3, 2, 4, 3, 2],
};

/**
 * Painel com os blocos reais da tela "Progresso do Usuário" do app
 * (`fragment_progress.xml`): os textos de destaque (desenho/rotina/música),
 * a lista de subcategorias mais clicadas, e as estatísticas de Jogos +
 * gráfico de Rotinas — os mesmos gráficos e rótulos o tempo todo, só os
 * valores variam devagar pra parecer um painel vivo.
 */
export function MonitoringLiveDemo() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [data, setData] = useState(initialData);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const timer = setInterval(() => {
      setData((d) => ({
        subcategoryCounts: d.subcategoryCounts.map((c) => randomWalk(c, 2, 3, 22)),
        gamesValues: [
          randomWalk(d.gamesValues[0], 3, 70, 98),
          randomWalk(d.gamesValues[1], 1, 0, 6),
          randomWalk(d.gamesValues[2], 3, 70, 98),
        ],
        chartValues: d.chartValues.map((v) => randomWalk(v, 1, 1, CHART_MAX)),
      }));
    }, 2600);
    return () => clearInterval(timer);
  }, [prefersReducedMotion]);

  return (
    <div className="rounded-3xl bg-primary/[0.05] dark:bg-primary/[0.1] p-6 md:p-8 grid md:grid-cols-2 gap-8" aria-hidden="true">
      {/* Coluna esquerda — destaques + categorias mais clicadas */}
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-2">
          {topStats.map((stat) => (
            <div key={stat.label} className="rounded-xl bg-background shadow-sm px-2.5 py-3">
              <div className="text-[10px] text-muted-foreground leading-tight mb-1">{stat.label}</div>
              <div className="text-sm font-bold text-foreground truncate">{stat.value}</div>
            </div>
          ))}
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2.5">
            Subcategorias Mais Ouvidas
          </h4>
          <div className="space-y-1.5">
            {subcategoriesBase.map((sub, i) => (
              <div key={sub.name} className="flex items-center gap-3 rounded-lg bg-background shadow-sm px-3 py-2">
                <img src={sub.icon} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
                <span className="text-sm font-medium text-foreground flex-1">{sub.name}</span>
                <span className="text-sm font-bold text-primary-ink dark:text-primary-tint tabular-nums">
                  {data.subcategoryCounts[i]}x
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Coluna direita — Jogos + Rotinas (gráfico fixo, só os dados mudam) */}
      <div className="space-y-6">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2.5">Jogos</h4>
          <div className="grid grid-cols-3 gap-2">
            {gamesStatsBase.map((stat, i) => (
              <div key={stat.label} className="rounded-xl bg-background shadow-sm px-2 py-3 text-center">
                <div className="text-xl font-bold tabular-nums transition-all" style={{ color: stat.color }}>
                  {data.gamesValues[i]}
                  {stat.suffix}
                </div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2.5">Frequência Mensal</h4>
          <div className="flex items-end justify-between gap-1 h-24">
            {months.map((month, i) => {
              const value = data.chartValues[i];
              return (
                <div key={month} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex items-end h-16">
                    <div
                      className="w-full rounded-sm bg-primary transition-all duration-700"
                      style={{ height: `${(value / CHART_MAX) * 100}%`, minHeight: value > 0 ? 4 : 0 }}
                    />
                  </div>
                  <span className="text-[8px] text-muted-foreground">{month}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
